import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { CustomerService } from "@/services/features/CustomerService";
import { Store } from "@/types/store";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { getBrowserTimezone } from "@/utils/storeTime";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "../services/StorePerformanceService";

export const useUnitsPerSession = (
  stores: Store[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  areaIds?: number[],
): AnalyticsCardData => {
  const areaIdsKey = areaIds?.join(",") ?? "";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawData, setRawData] = useState<{
    unitsPerSession: number;
    avgSessionValue: number;
    goalText: string;
    isPositive: boolean;
  } | null>(null);
  const beginRequest = useLatestRequestGuard();
  const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers(stores);
  const formatUnitsPerSession = (value: number) =>
    new Intl.NumberFormat(currentLanguage || undefined, {
      maximumSignificantDigits: 2,
    }).format(value);

  useEffect(() => {
    const isCurrent = beginRequest();
    const ids = stores?.map((store) => store.id) ?? [];
    if (ids.length === 0 || !dateRange) {
      setRawData(null);
      return;
    }

    const fetchUnitsPerSession = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const customerService = CustomerService.getInstance();
        const params: any = {
          storeIds: ids,
          startDate: storePerformanceService.formatDate(dateRange.start, browserTimezone),
          endDate: storePerformanceService.formatDate(dateRange.end, browserTimezone),
        };
        if (deviceType && deviceType !== "all") params.deviceType = deviceType;
        if (areaIdsKey) params.areaIds = areaIdsKey.split(",").map(Number);
        const response = await analyticsService.getSessionAnalytics(params);
        const sessionDenominator =
          deviceType === "IN_AISLE"
            ? response.data.totalSessionsIncludingNoItems
            : response.data.totalSessions;

        const unitsPerSession = sessionDenominator
          ? response.data.totalItems / sessionDenominator
          : 0;

        // For multi-store selection, we cannot use a single-store goal; omit goal/pill or keep generic.
        let goalText = "";
        let isPositive = true;
        if (ids.length === 1) {
          const storeGoalCustomerResponse = await customerService.getStoreGoal(ids[0]);
          if (storeGoalCustomerResponse.success) {
            const goal = storeGoalCustomerResponse.data?.upfvGoal || 0;
            goalText = `Goal ${storeGoalCustomerResponse.data?.upfvGoal}`;
            isPositive = unitsPerSession >= goal;
          }
        }

        if (!response.data || sessionDenominator === 0) {
          if (isCurrent()) setRawData(null);
          return;
        }
        if (!isCurrent()) return;
        setRawData({
          unitsPerSession,
          avgSessionValue: Math.round(response.data.totalSessionValue / sessionDenominator),
          goalText,
          isPositive,
        });
      } catch (error) {
        console.error("Error fetching units per session:", error);
        if (isCurrent()) setRawData(null);
      }
    };

    fetchUnitsPerSession();
    // Depend on primitive timestamps to avoid re-fetches caused by
    // new DateRange object identities on each render.
  }, [
    stores ? stores.map((store) => store.id).join(",") : null,
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    deviceType,
    areaIdsKey,
  ]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
      };
    }

    const avgSessionValue = formatCurrencyWithNoDecimals(
      rawData.avgSessionValue,
      currencyCode as CurrencyCode,
      currencySymbol,
    );

    return {
      previousValue: null,
      value: formatUnitsPerSession(rawData.unitsPerSession),
      subtitle: i18n.t("common.analytics.avgSessionValue", { value: avgSessionValue }),
      isPositive: rawData.isPositive,
      pillText: rawData.goalText,
      rawValue: rawData.unitsPerSession,
    };
  }, [rawData, currentLanguage, currencyCode, currencySymbol]);
};
